import React, { useState, useEffect, useMemo, useCallback } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
} from "reactstrap";
import { getZReport, exportZReportToExcel } from "../../components/Api/GmsZReport";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { Download } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  hasPagePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";

// Custom styles for Select components (from PayrollProcessingPage)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    businessId: authUser.businessId || user.user_Business_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
    companyId: authUser.companyId || user.user_Company_Id || null,
  };
};

export default function ZReportPage() {
  const [dayClosings, setDayClosings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [holidayFilter, setHolidayFilter] = useState(null); // null = All, false = Regular, true = Holiday
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsZReport');

  // Get user session
  const session = useMemo(() => getUserSession(), []);
  const userBusinessId = session.businessId;
  const userBranchId = session.branchId;
  const userCompanyId = session.companyId;

  // Load businesses
  const loadBusinesses = useCallback(async () => {
    try {
      const data = await getBusinesses({ status: true });
      setBusinesses(data || []);
      
      // Auto-select business from session if available
      if (userBusinessId && data) {
        const business = data.find(b => b.business_Id === userBusinessId);
        if (business) {
          setSelectedBusiness({
            value: business.business_Id,
            label: business.business_NameEn || business.business_Name
          });
        }
      }
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  }, [userBusinessId]);

  // Load branches
  const loadBranches = useCallback(async (businessId) => {
    try {
      if (!businessId) {
        setBranches([]);
        setSelectedBranch(null);
        return;
      }
      
      const data = await getBranches({ businessId: businessId, status: true });
      setBranches(data || []);
      
      // Auto-select branch from session if available
      if (userBranchId && data) {
        const branch = data.find(b => b.branch_Id === userBranchId);
        if (branch) {
          setSelectedBranch({
            value: branch.branch_Id,
            label: `${branch.branch_NameEn} - ${branch.branch_NameAr}`
          });
        }
      }
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  }, [userBranchId]);

  // Load Z Report data
  const loadZReportData = useCallback(async () => {
    if (!selectedBusiness) {
      setDayClosings([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    try {
      const filter = {
        businessId: selectedBusiness.value,
        branchId: selectedBranch?.value || null,
        fromDate: fromDate ? formatDateForAPI(fromDate, false) : null,
        toDate: toDate ? formatDateForAPI(toDate, false) : null,
        holiday: holidayFilter,
        page: page,
        pageSize: pageSize
      };

      const response = await getZReport(filter);
      setDayClosings(response.dayClosings || []);
      setTotalCount(response.totalCount || 0);
    } catch (error) {
      console.error("Error loading Z Report:", error);
      Swal.fire("Error", "Failed to load Z Report data: " + (error.response?.data?.message || error.message), "error");
      setDayClosings([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, selectedBranch, fromDate, toDate, holidayFilter, page, pageSize]);

  // Initial load
  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, loadBusinesses]);

  // Load branches when business changes
  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedBusiness, loadBranches]);

  // Load data when filters change
  useEffect(() => {
    if (selectedBusiness && hasPermission) {
      loadZReportData();
    }
  }, [selectedBusiness, selectedBranch, fromDate, toDate, holidayFilter, page, pageSize, loadZReportData, hasPermission]);

  // Business options
  const businessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id,
      label: b.business_NameEn || b.business_Name
    }));
  }, [businesses]);

  // Branch options
  const branchOptions = useMemo(() => {
    return branches.map(b => ({
      value: b.branch_Id,
      label: `${b.branch_NameEn} - ${b.branch_NameAr}`
    }));
  }, [branches]);

  // Holiday filter options
  const holidayOptions = [
    { value: null, label: 'All' },
    { value: false, label: 'Regular Closing' },
    { value: true, label: 'Holiday' }
  ];

  // Handle export to Excel
  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      const filter = {
        businessId: selectedBusiness.value,
        branchId: selectedBranch?.value || null,
        fromDate: fromDate ? formatDateForAPI(fromDate, false) : null,
        toDate: toDate ? formatDateForAPI(toDate, false) : null,
        holiday: holidayFilter
      };

      await exportZReportToExcel(filter);
      Swal.fire("Success", "Z Report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting Z Report:", error);
      Swal.fire("Error", "Failed to export Z Report: " + (error.response?.data?.message || error.message), "error");
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      return formatDateForDisplay(dateString, 'date');
    } catch (error) {
      return "N/A";
    }
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    try {
      return formatDateForDisplay(dateString, 'datetime');
    } catch (error) {
      return "N/A";
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Z Report" breadcrumbItem="POS & Billing" />
        
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="card-title mb-0">Z Report</h4>
                  <Button
                    color="success"
                    onClick={handleExportToExcel}
                    disabled={loading || !selectedBusiness || dayClosings.length === 0}
                  >
                    <Download size={18} className="me-2" />
                    Export to Excel
                  </Button>
                </div>

                {/* Filters */}
                <Card className="mb-4">
                  <CardBody>
                    <Row>
                      <Col md="3">
                        <Label>Business</Label>
                        <Select
                          options={businessOptions}
                          value={selectedBusiness}
                          onChange={(option) => {
                            setSelectedBusiness(option);
                            setSelectedBranch(null);
                            setPage(1);
                          }}
                          isClearable={!userBusinessId}
                          isDisabled={!!userBusinessId}
                          isSearchable
                          placeholder="Select Business"
                          styles={selectStyles}
                          maxMenuHeight={200}
                          menuPlacement="auto"
                        />
                      </Col>
                      <Col md="3">
                        <Label>Branch</Label>
                        <Select
                          options={branchOptions}
                          value={selectedBranch}
                          onChange={(option) => {
                            setSelectedBranch(option);
                            setPage(1);
                          }}
                          isClearable={!userBranchId}
                          isDisabled={!!userBranchId || !selectedBusiness}
                          isSearchable
                          placeholder="Select Branch"
                          styles={selectStyles}
                          maxMenuHeight={200}
                          menuPlacement="auto"
                        />
                      </Col>
                      <Col md="2">
                        <Label>From Date</Label>
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => {
                            setFromDate(date);
                            setPage(1);
                          }}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Select From Date"
                          isClearable
                        />
                      </Col>
                      <Col md="2">
                        <Label>To Date</Label>
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => {
                            setToDate(date);
                            setPage(1);
                          }}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Select To Date"
                          isClearable
                          minDate={fromDate}
                        />
                      </Col>
                      <Col md="2">
                        <Label>Type</Label>
                        <Select
                          options={holidayOptions}
                          value={holidayOptions.find(opt => opt.value === holidayFilter)}
                          onChange={(option) => {
                            setHolidayFilter(option.value);
                            setPage(1);
                          }}
                          isSearchable={false}
                          placeholder="All"
                          styles={selectStyles}
                          maxMenuHeight={200}
                          menuPlacement="auto"
                        />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Table Section */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      Day Closing Records ({totalCount} records)
                    </h5>
                    <div className="table-responsive">
                      <Table bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Sr.No</th>
                            <th>Closing Date</th>
                            <th>User</th>
                            <th>Business</th>
                            <th>Branch</th>
                            <th>Cash Total</th>
                            <th>Bank Total</th>
                            <th>Credit Total</th>
                            <th>Returns Total</th>
                            <th>Net Total</th>
                            <th>Holiday</th>
                            <th>Notes</th>
                            <th>Created Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="13" className="text-center">
                                <ClipLoader size={20} color="#556ee6" />
                                <span className="ms-2">Loading...</span>
                              </td>
                            </tr>
                          ) : dayClosings.length === 0 ? (
                            <tr>
                              <td colSpan="13" className="text-center">
                                No records found
                              </td>
                            </tr>
                          ) : (
                            dayClosings.map((item, index) => (
                              <tr key={item.dayClosingId}>
                                <td>{startIndex + index + 1}</td>
                                <td>{formatDate(item.closingDate)}</td>
                                <td>{item.userName || 'N/A'}</td>
                                <td>{item.businessName || 'N/A'}</td>
                                <td>{item.branchName || 'N/A'}</td>
                                <td>{formatCurrency(item.cashTotal)}</td>
                                <td>{formatCurrency(item.cashlessTotal)}</td>
                                <td>{formatCurrency(item.creditTotal)}</td>
                                <td>{formatCurrency(item.returnsTotal)}</td>
                                <td>{formatCurrency(item.netTotal)}</td>
                                <td>
                                  <span className={`badge ${item.holiday ? 'bg-warning' : 'bg-success'}`}>
                                    {item.holiday ? 'Holiday' : 'Regular'}
                                  </span>
                                </td>
                                <td>{item.notes || '-'}</td>
                                <td>{formatDateTime(item.createdDate)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {totalCount > pageSize && (
                      <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                           style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                        <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                          <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                          Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? startIndex + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{endIndex}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                            }}
                          >
                            <i className="mdi mdi-chevron-left"></i>
                          </Button>
                          
                          <span style={{fontSize: '14px', fontWeight: '500', color: '#495057', minWidth: '60px', textAlign: 'center'}}>
                            Page {page} of {totalPages}
                          </span>
                          
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === totalPages}
                            onClick={() => handlePageChange(page + 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                            }}
                          >
                            <i className="mdi mdi-chevron-right"></i>
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

